/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */
import {type PreloadedQuery, graphql, useFragment} from 'react-relay'
import type {ProjectItemSectionFieldsQuery} from './__generated__/ProjectItemSectionFieldsQuery.graphql'
import {useMemo} from 'react'
import {SingleSelectField} from '../projects/SingleSelectField'
import {TextField} from '../projects/TextField'
import {NumberField} from '../projects/NumberField'
import {IterationField} from '../projects/IterationField'
import {DateField} from '../projects/DateField'
import type {ProjectItemSectionFields$key} from './__generated__/ProjectItemSectionFields.graphql'
import type {ProjectItemSectionFieldsValues$key} from './__generated__/ProjectItemSectionFieldsValues.graphql'
import {Box} from '@primer/react'

export type ProjectItemSectionFieldsProps = {
  onIssueUpdate?: () => void
  projectItemId: string
  projectItemsRef: PreloadedQuery<ProjectItemSectionFieldsQuery>
}

type ProjectItemSectionFieldsPropsInternal = {
  projectItem: ProjectItemSectionFields$key
  onIssueUpdate?: () => void
}

export const ProjectItemSectionFieldsGraphqlQuery = graphql`
  query ProjectItemSectionFieldsQuery($owner: String!, $repo: String!, $number: Int!, $allowedOwner: String) {
    repository(owner: $owner, name: $repo) {
      issueOrPullRequest(number: $number) {
        ... on Issue {
          id
          viewerCanUpdateNext
          projectItemsNext(first: 10, allowedOwner: $allowedOwner) @connection(key: "ProjectSection_projectItemsNext") {
            edges {
              node {
                id
                ...ProjectItemSection
              }
            }
          }
        }
        ... on PullRequest {
          id
          viewerCanUpdate
          projectItemsNext(first: 10, allowedOwner: $allowedOwner) @connection(key: "ProjectSection_projectItemsNext") {
            edges {
              node {
                id
                ...ProjectItemSection
              }
            }
          }
        }
      }
    }
  }
`

export const ProjectItemSectionFields = graphql`
  fragment ProjectItemSectionFields on ProjectV2Item {
    id
    isArchived
    project {
      id
      title
      url
      viewerCanUpdate
      fields(first: 50, orderBy: {field: POSITION, direction: ASC}) {
        edges {
          node {
            ... on ProjectV2Field {
              id
              dataType
              name
              ...TextFieldConfigFragment
              ...DateFieldConfigFragment
              ...NumberFieldConfigFragment
            }
            ... on ProjectV2IterationField {
              id
              dataType
              name
              ...IterationFieldConfigFragment
            }
            ... on ProjectV2SingleSelectField {
              id
              dataType
              name
              ...SingleSelectFieldConfigFragment
            }
          }
        }
      }
    }
    ...ProjectItemSectionFieldsValues
  }
`

export function ProjectItemSectionFieldsInternal({projectItem, onIssueUpdate}: ProjectItemSectionFieldsPropsInternal) {
  const projectItemFields = useFragment(ProjectItemSectionFields, projectItem)
  let fieldsCount = 0

  const {fieldValues} = useFragment<ProjectItemSectionFieldsValues$key>(
    graphql`
      fragment ProjectItemSectionFieldsValues on ProjectV2Item {
        fieldValues(first: 50, orderBy: {field: POSITION, direction: ASC})
          @connection(key: "ProjectItemSection_fieldValues") {
          edges {
            node {
              ... on ProjectV2ItemFieldTextValue {
                id
                field {
                  ... on ProjectV2Field {
                    id
                  }
                }
              }
              ...TextFieldFragment
              ... on ProjectV2ItemFieldNumberValue {
                id
                field {
                  ... on ProjectV2Field {
                    id
                  }
                }
              }
              ...NumberFieldFragment
              ... on ProjectV2ItemFieldDateValue {
                id
                field {
                  ... on ProjectV2Field {
                    id
                  }
                }
              }
              ...DateFieldFragment
              ... on ProjectV2ItemFieldSingleSelectValue {
                id
                field {
                  ... on ProjectV2SingleSelectField {
                    id
                  }
                }
              }
              ...SingleSelectFieldFragment
              ... on ProjectV2ItemFieldIterationValue {
                id
                field {
                  ... on ProjectV2IterationField {
                    id
                  }
                }
              }
              ...IterationFieldFragment
            }
          }
        }
      }
    `,
    projectItemFields,
  )

  const fields = useMemo(
    () =>
      (projectItemFields?.project.fields?.edges || []).map(edge => {
        if (!edge?.node || edge.node.name === 'Status') {
          return
        }

        const field = edge.node
        const dataType = field.dataType

        if (!fieldsCount && dataType) {
          const dataTypes = ['TEXT', 'SINGLE_SELECT', 'DATE', 'NUMBER', 'ITERATION']
          dataTypes.includes(dataType) && fieldsCount++
        }

        const fieldId = field.id
        const value =
          (fieldValues?.edges || []).flatMap(e => (e?.node ? [e.node] : [])).find(e => e?.field?.id === fieldId) || null

        const props = {
          key: fieldId,
          viewerCanUpdate: !projectItemFields.isArchived && projectItemFields.project.viewerCanUpdate,
          itemId: projectItemFields.id,
          projectId: projectItemFields.project.id,
          field,
          value,
          onIssueUpdate,
        }

        switch (dataType) {
          case 'SINGLE_SELECT':
            return <SingleSelectField {...props} />
          case 'TEXT':
            return <TextField {...props} />
          case 'DATE':
            return <DateField {...props} />
          case 'NUMBER':
            return <NumberField {...props} />
          case 'ITERATION':
            return <IterationField {...props} />
        }
      }),
    [
      projectItemFields.project.fields?.edges,
      projectItemFields.project.viewerCanUpdate,
      projectItemFields.project.id,
      projectItemFields.isArchived,
      projectItemFields.id,
      fieldsCount,
      fieldValues?.edges,
      onIssueUpdate,
    ],
  )

  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: fieldsCount ? 'border.default' : 'transparent',
        pt: fieldsCount ? '9px' : 0,
        mt: fieldsCount ? '9px' : 0,
        display: fieldsCount ? 'block' : 'none',
      }}
    >
      <Box sx={{px: '12px'}}>{fields}</Box>
    </Box>
  )
}

try{ ProjectItemSectionFieldsInternal.displayName ||= 'ProjectItemSectionFieldsInternal' } catch {}